/*
 *
 * Copyright 2021, White River Computing LLC. All rights reserved.
 * For licensing information, please contact Charlie Flock (cflock@whiterivercomputing.com or 888-802-6340)
 */

import './WrcForm.css';

import React from "react";
import ReactDOM from "react-dom";
import PropTypes from "prop-types";
import {Button, Checkbox, ControlGroup, Dialog, Radio, RadioGroup, HTMLTable, Classes, Position} from "@blueprintjs/core";
import {Elevation, Card, Tab, Tabs, H4, H6, TextArea, MenuItem, Callout, HTMLSelect, Alignment, Alert} from "@blueprintjs/core";
import {Suggest} from "@blueprintjs/select";
import {Intent} from "@blueprintjs/core/lib/esm/index";
import {IconNames} from "@blueprintjs/icons/lib/esm/index";
import {DateInput} from "@blueprintjs/datetime";
import {Classes as Popover2Classes, Tooltip2} from "@blueprintjs/popover2";
import {WrcGlobal} from "./WrcGlobal.js";
import {WrcServer} from "./WrcServer.js";
import {WrcVAS} from "./WrcVAS.js";

export class WrcForm extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            is_loaded: false,
            has_changed: false,
            form: {tabs: []},
            tabs_visited: {},
            data: {},
            show_confirm: false
        };
    }

    componentDidMount() {
        let payload = {form_id: this.props.form_id};

        if (this.props.fkey !== undefined) {
            payload['fkey'] = this.props.fkey;
        }
        if (this.props.record_id !== undefined) {
            payload['record_id'] = this.props.record_id;
        }
        WrcServer.post('get-form',
                payload,
                (content) => this.getForm(content)
        );
    }

    render() {
        if (this.state.is_loaded) {
            if (this.state.show_confirm) {
                return (
                    <Dialog
                        isOpen={true}
                        onClose={this.cancelSave}
                        canOutsideClickClose={false}
                        title={this.state.form.form_name}
                        id="web_form"
                        style={{minWidth: 1100, minHeight: 600, maxWidth: 1400, maxHeight: 800, overflow: 'scroll'}} >
                        <div className={Classes.DIALOG_BODY}>
                            <Card elevation={Elevation.ZERO}>
                                <p>After checking the document, would you like to continue?</p>
                            </Card>
                        </div>
                        <div className={Classes.DIALOG_FOOTER}>
                            <div className={'bp3-dialog-footer-actions'} style={{float: 'right', padding: 10}}>
                                <Button onClick={this.cancelSave} text="No" type={'button'}/>
                                <Button intent={Intent.SUCCESS} text="Yes" type={'submit'} onClick={this.confirmSave}/>
                            </div>
                        </div>
                    </Dialog>
                );
            }
            let is_vertical = false;
            if (this.state.form.layout === 'vertical') {
                is_vertical = true;
            }
            let has_record_complete_checkbox = false;
            if (this.state.form.has_record_complete_checkbox) {
                has_record_complete_checkbox = this.state.form.has_record_complete_checkbox;
            }
            let pages = [];
            this.state.form.tabs.forEach(function(tab, tab_index) {
                pages[tab_index] = [];

                tab.elements.forEach(function(element, element_index) {
                    if (element.show_if !== undefined) {
                        for (let i = 0; i < element.show_if.length; i++) {
                            if (this.matchNotFound(element.show_if[i], this.state)) {
                                return;
                            }
                        }
                    }

                    let disabled = this.props.readonly;
                    if (!disabled && element.disable_if !== undefined) {
                        disabled = this.shouldBeDisabled(element, this.state);
                    }

                    let addendum = '';
                    if (element.include_not_reported) {
                        let field_has_data = false;
                        if (element.column_name in this.state.data &&
                            this.state.data[element.column_name] !== null &&
                            this.state.data[element.column_name] !== '') {
                                field_has_data = true;
                        }
                        addendum = (
                            <Checkbox
                                    inline={true}
                                    label={element.include_not_reported == true ? "Not reported" : element.include_not_reported}
                                    style={{marginLeft: 30}}
                                    checked={this.state.data[element.column_name + '_not_reported'] === 't'}
                                    disabled={disabled || field_has_data}
                                    name={element.column_name + '_not_reported'}
                                    onChange={this.handleNotReported(element)} />

                        );
                        if (this.state.data[element.column_name + '_not_reported'] === 't') {
                            disabled = true;
                        }
                    }

                    // checks for legacy data
                    let response_values = [];
                    let value_in_responses = undefined;
                    let legacy_val = undefined;
                    let new_boxes= [];
                    let missing_checkbox_options = [];
                    let default_value = '';

                    switch (element.element_type) {
                        case 'today':
                        case 'timestamp':
                        case 'datestamp':
                            break;
                        case 'img':
                            pages[tab_index].push(<tr><td colspan="100"><img src={"img/"+element.content} style={element.style} alt=''/></td></tr>);
                            break;
                        case 'display':
                            let source = 'data';
                            if (element.source !== undefined) {
                                source = element.source;
                            }
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td style={element.style}>
                                            <div class="wd-form-label">{element.label}</div>
                                            <div class="wd-form-inputgroup-vertical-layout">
                                                <div class="bp3-control-group bp3-vertical">
                                                {this.state[source][element.column_name]}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td style={element.style}>
                                            {this.state[source][element.column_name]}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'instructions':
                            let formattedInstructions = (
                                <tr>
                                    <td colspan="100" className={'linebreak-text'}>
                                        <H6 style={element.style}>{element.label}</H6>
                                        {element.content}
                                    </td>
                                </tr>
                            );
                            pages[tab_index].push(formattedInstructions);
                            break;
                        case 'boolean':
                            let booleanBox=(
                                <Checkbox
                                    inline={!is_vertical}
                                    large= {false}
                                    key={'element_index_' + element_index}
                                    label={element.label}
                                    checked={this.state.data[element.column_name] ? this.state.data[element.column_name] : false}
                                    onChange={(e) => this.handleBooleanChange(e, element)}
                                    disabled={disabled}
                                />
                            );
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout'}>
                                              {booleanBox}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout'}>
                                            {booleanBox}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'radio':
                            response_values= element.responses.map((response) => response.value);
                            value_in_responses= (this.state.data[element.column_name] === undefined ||
                                this.state.data[element.column_name] === null || response_values.includes(this.state.data[element.column_name]));
                            if(!value_in_responses){
                                legacy_val=this.state.data[element.column_name];
                            }
                            if (element.default_if !== undefined) {
                                for (let i = 0; i < element.default_if.length; i++) {
                                    if (!this.matchNotFound(element.default_if[i], this.state)) {
                                        default_value = element.default_if[i].default;
                                        break;
                                    }
                                }
                            }

                            let radioOptionList = element.responses.filter(function(response) {
                                
                                if (element.do_not_show_responses_if !== undefined) {
                                    for (let i = 0; i < element.do_not_show_responses_if.length; i++) {
                                        if (this.state.data[element.do_not_show_responses_if[i].field_name] !== undefined && this.state.data[element.do_not_show_responses_if[i].field_name] !== "") {
                                            if (eval(this.state.data[element.do_not_show_responses_if[i].field_name] + ' ' + element.do_not_show_responses_if[i].condition)) {
                                                if (element.do_not_show_responses_if[i].hide_values.includes(response.value)) {
                                                    return false
                                                }
                                            }
                                        }
                                    }
                                }
                                if (response.show_if !== undefined) {
                                    for (let i = 0; i < response.show_if.length; i++) {
                                        if (this.matchNotFound(response.show_if[i], this.state) &&
                                            response.value !== this.state.data[element.column_name]) {
                                            return false;
                                        }
                                    }
                                }
                                return true;
                            }.bind(this)).map(function(response, response_index) {
                                let button_disabled = disabled;
                                if (response.show_if !== undefined) {
                                    for (let i = 0; i < response.show_if.length; i++) {
                                        if (this.matchNotFound(response.show_if[i], this.state)) {
                                            button_disabled = true;
                                            break;
                                        }
                                    }
                                }
                                return (
                                    <Radio
                                            inline={!is_vertical}
                                            disabled={button_disabled}
                                            key={element.column_name + ':' + response_index}
                                            label={response.label ? response.label : response.value}
                                            value={response.value} />
                                );
                            }.bind(this));

                            let responses = (
                                <ControlGroup fill={false} vertical={is_vertical}>
                                    <RadioGroup
                                            key={element.column_name}
                                            name={element.column_name}
                                            inline={!is_vertical}
                                            selectedValue={this.state.data[tab.elements[element_index].column_name] || default_value}
                                            onChange={this.handleInputChange}>
                                        {radioOptionList}
                                        {!value_in_responses &&
                                            <Radio
                                                    inline={!is_vertical}
                                                    disabled={true}
                                                    key={element.column_name + ':' + 'legacy'}
                                                    label={legacy_val}
                                                    value={legacy_val} />}
                                    </RadioGroup>
                                </ControlGroup>
                            );

                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout'}>
                                                {responses}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout'}>
                                            {responses}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'checkbox':
                            response_values= element.responses.map((response) => response.value);
                            missing_checkbox_options= (this.state.data[element.table_name] !== undefined ? this.state.data[element.table_name].filter((opt) => response_values.indexOf(opt) == -1) : []);
                            value_in_responses= (missing_checkbox_options.length === 0);
                            new_boxes= missing_checkbox_options.map((box) =>{
                                return (<Checkbox
                                        inline={!is_vertical}
                                        disabled={disabled}
                                        key={'response-' + 'legacy'}
                                        label={box}
                                        checked={this.isChecked(element.table_name, box)}
                                        onChange={this.handleCheckbox(element, {'value': box})}/>)
                            });

                            let checkboxes = element.responses.filter(function(response) {
                                if (response.show_if !== undefined) {
                                    for (let i = 0; i < response.show_if.length; i++) {
                                        if (this.matchNotFound(response.show_if[i], this.state)) {
                                            return false;
                                        }
                                    }
                                }
                                return true;
                            }.bind(this)).map(function(response) {
                                let disabled = this.props.readonly;
                                if (!disabled && response.disable_if !== undefined) {
                                    disabled = this.shouldBeDisabled(response, this.state);
                                }
                                return (
                                    <Checkbox
                                            inline={!is_vertical}
                                            disabled={disabled}
                                            key={'response-' + response.id}
                                            label={response.label ? response.label : response.value}
                                            checked={this.isChecked(element.table_name, response.value)}
                                            onChange={this.handleCheckbox(element, response)} />
                                );
                            }.bind(this));

                            let check_responses=(
                                <ControlGroup fill={false} vertical={true}>
                                    {checkboxes}
                                    {!value_in_responses &&
                                        new_boxes
                                    }
                                </ControlGroup>
                            );

                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout'}>
                                              {check_responses}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout'}>
                                            {check_responses}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'select':
                            response_values= element.responses.map((response) => response.value);
                            value_in_responses= (this.state.data[element.column_name] === undefined ||
                                this.state.data[element.column_name] === null || response_values.includes(this.state.data[element.column_name]));
                            if(!value_in_responses){
                                legacy_val=this.state.data[element.column_name];
                            }

                            let selectOptionList = element.responses.filter(function(response) {
                                if (response.show_if !== undefined) {
                                    for (let i = 0; i < response.show_if.length; i++) {
                                        if (this.matchNotFound(response.show_if[i], this.state)) {
                                            return false;
                                        }
                                    }
                                }
                                return true;
                            }.bind(this)).map(function(response) {
                                if (response.disabled) {
                                    return (
                                        <option disabled={true}>{response.label ? response.label : response.value}</option>
                                    );
                                } else {
                                    return (
                                        <option value={response.value}>{response.label ? response.label : response.value}</option>
                                    );
                                }
                            });

                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout wd-form-select'}>
                                                <HTMLSelect
                                                        name={element.column_name}
                                                        disabled={disabled}
                                                        value={(this.state.data[element.column_name] === null || this.state.data[element.column_name] === undefined) ? '' : this.state.data[element.column_name]}
                                                        onChange={this.handleInputChange}>
                                                    {selectOptionList.length > 1 ? (<option value="">Select...</option>) : null}
                                                    {selectOptionList}
                                                    {!value_in_responses && legacy_val !== "" &&
                                                        <option value={legacy_val}>{legacy_val}</option>}
                                                </HTMLSelect>
                                                {addendum}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout wd-form-select'}>
                                            <HTMLSelect
                                                    name={element.column_name}
                                                    disabled={disabled}
                                                    value={(this.state.data[element.column_name] === null || this.state.data[element.column_name] === undefined) ? '' : this.state.data[element.column_name]}
                                                    onChange={this.handleInputChange}>
                                                {selectOptionList.length > 1 ? (<option value="">Select...</option>) : null}
                                                {selectOptionList}
                                            </HTMLSelect>
                                            {addendum}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'select-suggest':
                            let selectSuggest = this.selectSuggest(element, disabled);
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout wd-form-select'} style={element.style}>
                                                {selectSuggest}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            } else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout wd-form-select'} style={element.style}>
                                            {selectSuggest}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'month':
                            let month_responses = (
                                <HTMLSelect
                                        name={element.column_name}
                                        disabled={disabled}
                                        value={(this.state.data[element.column_name] === null || this.state.data[element.column_name] === undefined) ? '' : this.state.data[element.column_name]}
                                        onChange={this.handleInputChange}>
                                    <option value="">Select...</option>
                                    <option value="1">January</option>
                                    <option value="2">February</option>
                                    <option value="3">March</option>
                                    <option value="4">April</option>
                                    <option value="5">May</option>
                                    <option value="6">June</option>
                                    <option value="7">July</option>
                                    <option value="8">August</option>
                                    <option value="9">September</option>
                                    <option value="10">October</option>
                                    <option value="11">November</option>
                                    <option value="12">December</option>
                                </HTMLSelect>
                            );
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout wd-form-select'}>
                                                {month_responses}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout wd-form-select'}>
                                            <div>
                                                {month_responses}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'year':
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout'}>
                                                <input className={'bp3-input'} disabled={disabled} name={element.column_name} type="number" maxlength="4" value={this.state.data[element.column_name]} style={element.style} onKeyPress={(event) => {if (!/[0-9]/.test(event.key)) {event.preventDefault();}}} onChange={this.handleInputChange} />
                                                {addendum}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout'}>
                                            <input className={'bp3-input'} disabled={disabled} name={element.column_name} type="number" maxlength="4" value={this.state.data[element.column_name]} style={element.style} onKeyPress={(event) => {if (!/[0-9]/.test(event.key)) {event.preventDefault();}}} onChange={this.handleInputChange} />
                                            {addendum}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'vas':
                            pages[tab_index].push(
                                <tr>
                                    <td>
                                        <WrcVAS
                                                element={element}
                                                data={this.state.data}
                                                recordData={this.recordData} />
                                    </td>
                                </tr>
                            );
                            break;
                        case 'textarea':
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.label}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout'}>
                                                <TextArea
                                                    name={element.column_name}
                                                    growVertically={true}
                                                    large={false}
                                                    onChange={this.handleInputChange}
                                                    value={this.state.data[element.column_name]}
                                                    style={element.style}
                                                    fill={true}
                                                    disabled={disabled}
                                                />
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td className={'wd-form-inputgroup-default-layout'}>
                                            <TextArea
                                                name={element.column_name}
                                                growVertically={true}
                                                large={false}
                                                onChange={this.handleInputChange}
                                                value={this.state.data[element.column_name]}
                                                style={element.style}
                                                fill={true}
                                                disabled={disabled}
                                            />
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        case 'table':
                            let headers = element.columns.filter(function(column) {
                                if (column.show_column_if !== undefined) {
                                    for (let i = 0; i < column.show_column_if.length; i++) {
                                        if (this.matchNotFound(column.show_column_if[i], this.state)) {
                                            return false;
                                        }
                                    }
                                }
                                return true;
                            }.bind(this)).map(function(column) {
                                let column_class = column.column_class || '';
                                let column_style = typeof(column.column_style) === 'object' ? column.column_style : {};
                                return (
                                    <th className={column_class} style={column_style}>
                                        {column.label}
                                    </th>
                                );
                            });
                            let rows = [];
                            if (Array.isArray(this.state.data[element.table_name])) {
                                rows = this.state.data[element.table_name].map(function(row, row_index) {
                                    let columns = element.columns.filter(function(column, column_index) {
                                        if (column.show_column_if !== undefined) {
                                            for (let i = 0; i < column.show_column_if.length; i++) {
                                                if (this.matchNotFound(column.show_column_if[i], this.state)) {
                                                    return false;
                                                }
                                            }
                                        }
                                        return true;
                                    }.bind(this)).map(function(column, column_index) {
                                        if (column.show_if !== undefined) {
                                            for (let i = 0; i < column.show_if.length; i++) {
                                                if (this.matchNotFound(column.show_if[i], this.state, element.table_name, row_index)) {
                                                    return (<td>&nbsp;</td>);
                                                }
                                            }
                                        }

                                        let disabled = this.props.readonly;
                                        if (!disabled && column.disable_if !== undefined) {
                                            disabled = this.shouldBeDisabled(column, this.state, element.table_name, row_index);
                                        }

                                        let column_class = column.column_class || '';
                                        let column_style = typeof(column.column_style) === 'object' ? column.column_style : {};
                                        let input_class = '';
                                        if (column.input_class) {
                                            input_class = column.input_class;
                                        }
                                        let input_style = typeof(column.input_style) === 'object' ? column.input_style : {};

                                        let groupKey = element.table_name + '.' + column.column_name + ':' + row_index;

                                        switch (column.element_type) {
                                            case 'today':
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        {this.state.data[element.table_name][row_index][column.column_name]}
                                                    </td>
                                                );
                                            case 'timestamp':
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        {this.state.data[element.table_name][row_index][column.column_name]}
                                                    </td>
                                                );
                                            case 'datestamp':
                                                let display_value = this.state.data[element.table_name][row_index][column.column_name];
                                                if (display_value) {
                                                    display_value = (
                                                        <Tooltip2 className={Popover2Classes.TOOLTIP2_INDICATOR} content={display_value} intent={Intent.PRIMARY} placement="right">
                                                            {display_value.substring(0, 10)}
                                                        </Tooltip2>
                                                    );
                                                }
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        {display_value}
                                                    </td>);
                                            case 'boolean':
                                                let booleanBox=(
                                                    <Checkbox
                                                        key={'column_' + column_index}
                                                        large= {false}
                                                        disabled={disabled}
                                                        style={{"textAlign": "center", "top": "6px", "left": "6px"}}
                                                        checked={this.state.data[element.table_name][row_index][column.column_name] ? this.state.data[element.table_name][row_index][column.column_name] : false}
                                                        onChange={(e) => this.handleBooleanChange(e, element, row_index, column)} />
                                                );
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        {booleanBox}
                                                    </td>
                                                );
                                            case 'radio':
                                                response_values= column.responses.map((response) => response.value);
                                                value_in_responses= (this.state.data[element.table_name][row_index][column.column_name] === undefined || this.state.data[element.table_name][row_index][column.column_name] === null || response_values.includes(this.state.data[element.table_name][row_index][column.column_name]));
                                                if(!value_in_responses){
                                                    legacy_val=this.state.data[element.table_name][row_index][column.column_name];
                                                }

                                                let optionList = column.responses.map(function(response, response_index) {
                                                    return (
                                                        <Radio
                                                                inline={!column.vertical}
                                                                disabled={disabled}
                                                                key={groupKey + ':' + response_index}
                                                                label={response.label ? response.label : response.value}
                                                                value={response.value} />
                                                    );
                                                });
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        <ControlGroup fill={false} vertical={column.vertical}>
                                                            <RadioGroup
                                                                    key={groupKey}
                                                                    inline={!column.vertical}
                                                                    selectedValue={this.state.data[element.table_name][row_index][column.column_name]}
                                                                    onChange={this.handleChildInputChange(element, row_index, column)}>
                                                                {optionList}
                                                                {!value_in_responses &&
                                                                    <Radio
                                                                            inline={!column.vertical}
                                                                            disabled={disabled}
                                                                            key={groupKey + ':' + 'legacy'}
                                                                            label={legacy_val}
                                                                            value={legacy_val} />}
                                                            </RadioGroup>
                                                        </ControlGroup>
                                                    </td>
                                                );
                                            case 'select':
                                                response_values= column.responses.map((response) => response.value);
                                                value_in_responses= (this.state.data[element.table_name][row_index][column.column_name] === undefined || this.state.data[element.table_name][row_index][column.column_name] === null || response_values.includes(this.state.data[element.table_name][row_index][column.column_name]));
                                                if(!value_in_responses){
                                                    legacy_val=this.state.data[element.table_name][row_index][column.column_name];
                                                }

                                                let selectOptionList = column.responses.filter(function(response) {
                                                    if (response.show_if !== undefined) {
                                                        for (let i = 0; i < response.show_if.length; i++) {
                                                            if (this.matchNotFound(response.show_if[i], this.state, element.table_name, row_index)) {
                                                                return false;
                                                            }
                                                        }
                                                    }
                                                    return true;
                                                }.bind(this)).map(function(response) {
                                                    return (
                                                        <option value={response.value}>{response.label ? response.label : response.value}</option>
                                                    );
                                                });
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        <HTMLSelect
                                                                name={groupKey}
                                                                className={input_class + ' bp3-fill'}
                                                                style={input_style}
                                                                disabled={disabled}
                                                                value={(this.state.data[element.table_name][row_index][column.column_name] === null || this.state.data[element.table_name][row_index][column.column_name] === undefined) ? '' : this.state.data[element.table_name][row_index][column.column_name]}
                                                                onChange={this.handleChildInputChange(element, row_index, column)}>
                                                            {selectOptionList.length > 1 ? (<option value="">Select...</option>) : null}
                                                            {selectOptionList}
                                                            {!value_in_responses &&
                                                                <option value={legacy_val}>{legacy_val} </option>}
                                                        </HTMLSelect>
                                                    </td>
                                                );
                                            case 'select-suggest':
                                                let selectSuggest = this.selectSuggest(column, disabled, true, element.table_name, row_index);
                                                return (
                                                    <td className={'wd-form-select ' + column_class} style={column_style}>
                                                        {selectSuggest}
                                                    </td>
                                                );

                                            default:
                                                let addendum = '';
                                                if (column.include_not_reported) {
                                                    addendum = (
                                                        <Checkbox
                                                            label={column.include_not_reported == true ? "Not reported" : column.include_not_reported}
                                                            checked={this.state.data[element.table_name][row_index][column.column_name + '_not_reported'] === 't'}
                                                            onChange={this.handleNotReportedInTable(element, row_index, column)}
                                                            name={column.column_name + '_not_reported'}
                                                            className={input_class}
                                                            style={input_style}
                                                            disabled={disabled}
                                                        />
                                                    );
                                                    if (this.state.data[element.table_name][row_index][column.column_name + '_not_reported'] === 't') {
                                                        disabled = true;
                                                    }
                                                }

                                                input_class += ' bp3-input';
                                                return (
                                                    <td className={column_class} style={column_style}>
                                                        <input
                                                            disabled={disabled}
                                                            className={input_class}
                                                            style={input_style}
                                                            type={column.element_type}
                                                            value={this.state.data[element.table_name][row_index][column.column_name]}
                                                            onChange={this.handleChildInputChange(element, row_index, column)}
                                                            autocapitalize={column.autocapitalize || 'none'}
                                                            placeholder={column.placeholder}
                                                            min={column.min_value}
                                                            max={column.max_value}
                                                        />
                                                        {addendum}
                                                    </td>
                                                );
                                        }
                                    }.bind(this));
                                    let deleteCell = null;
                                    if (this.props.readonly !== true) {
                                        deleteCell = (
                                            <td>
                                                <Button icon={IconNames.DELETE} minimal={true} onClick={(event) => this.deleteRecord(event, element, row_index)}/>
                                            </td>
                                        );
                                    }
                                    return (
                                        <tr>
                                            {columns}
                                            {deleteCell}
                                        </tr>
                                    );
                                }.bind(this));
                            }

                            let add_button = null;
                            if ((element.max_records === undefined ||
                                !(element.table_name in this.state.data) ||
                                this.state.data[element.table_name].length < element.max_records) &&
                                this.props.readonly !== true) {
                                add_button = (
                                    <tr>
                                        <td colSpan={headers.length + 1} style={{textAlign: 'center'}}>
                                            <Button icon={IconNames.ADD}
                                                    intent={Intent.PRIMARY}
                                                    text='Add'
                                                    onClick={(event) => this.addRecord(event, element)}
                                                    disabled={disabled}
                                                    className='add-button'
                                                    />
                                        </td>
                                    </tr>
                                );
                            }

                            let deleteHeader = null;
                            if (this.props.readonly !== true) {
                                deleteHeader = (<th></th>);
                            }
                            let table_element = (
                                <table className={'bp3-html-table bp3-html-table-bordered'} style={{border: '1px solid lightgray', 'minWidth':'50%'}}>
                                    <thead>
                                        <tr>
                                            {headers}
                                            {deleteHeader}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows}
                                        {add_button}
                                    </tbody>
                                </table>
                            );

                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div className={'wd-form-label'}>{element.title}</div>
                                            <div className={'wd-form-inputgroup-vertical-layout wd-form-table'}>
                                                {table_element}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            } else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.title}</td>
                                        <td className={'wd-form-inputgroup-default-layout wd-form-table'}>
                                            {table_element}
                                        </td>
                                    </tr>
                                );
                            }

                            break;
                        case 'date':
                            // Allow date control to range from 1900 to the end of the year 20 years in the future
                            let minDate = new Date(1900, 0, 1);
                            let maxDate = new Date(new Date().getFullYear() + 20, 11, 31);
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div class="wd-form-label">{element.label}</div>
                                            <div class="wd-form-inputgroup-vertical-layout">
                                                <div class="bp3-control-group bp3-vertical">
                                                    <DateInput
                                                        disabled={disabled}
                                                        minDate={minDate}
                                                        maxDate={maxDate}
                                                        formatDate={date => this.formatDate(date)}
                                                        parseDate={str => this.parseDate(str)}
                                                        onChange={(selectedDate, isUserChange) => this.handleDateChange({newDate: selectedDate, field_name: element.column_name})}
                                                        placeholder={"YYYY-MM-DD"}
                                                        value={this.state.data[element.column_name] ? this.stringToDate(this.state.data[element.column_name]) : null}
                                                        popoverProps={{ position: Position.BOTTOM }}
                                                        showActionsBar={true}
                                                    />
                                                </div>
                                                {addendum}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td>
                                            <DateInput
                                                disabled={disabled}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                formatDate={date => this.formatDate(date)}
                                                parseDate={str => this.parseDate(str)}
                                                onChange={(selectedDate, isUserChange) => this.handleDateChange({newDate: selectedDate, field_name: element.column_name})}
                                                placeholder={"YYYY-MM-DD"}
                                                value={this.state.data[element.column_name] ? this.stringToDate(this.state.data[element.column_name]) : null}
                                                popoverProps={{ position: Position.BOTTOM }}
                                                showActionsBar={true}
                                            />
                                            {addendum}
                                        </td>
                                    </tr>
                                );
                            }
                            break;
                        default:
                            if (element.default_if !== undefined) {
                                for (let i = 0; i < element.default_if.length; i++) {
                                    if (!this.matchNotFound(element.default_if[i], this.state)) {
                                        default_value = element.default_if[i].default;
                                        break;
                                    }
                                }
                            }
                            if (is_vertical) {
                                pages[tab_index].push(
                                    <tr>
                                        <td>
                                            <div class="wd-form-label">{element.label}</div>
                                            <div class="wd-form-inputgroup-vertical-layout">
                                                <div class="bp3-control-group bp3-vertical">
                                                <input className={'bp3-input'}
                                                    disabled={disabled}
                                                    name={element.column_name}
                                                    type={element.element_type}
                                                    value={this.state.data[element.column_name] ? this.state.data[element.column_name] : default_value}
                                                    style={element.style}
                                                    readOnly={element.readonly}
                                                    onChange={this.handleInputChange}
                                                    autocapitalize={element.autocapitalize || 'none'}
                                                    placeholder={element.placeholder}
                                                    min={element.min_value}
                                                    max={element.max_value}
                                                />
                                                </div>
                                                {addendum}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            }
                            else {
                                pages[tab_index].push(
                                    <tr>
                                        <td className={'wd-form-label'}>{element.label}</td>
                                        <td>
                                            <input className={'bp3-input'}
                                                disabled={disabled}
                                                name={element.column_name}
                                                type={element.element_type}
                                                value={this.state.data[element.column_name] ? this.state.data[element.column_name] : default_value}
                                                style={element.style}
                                                readOnly={element.readonly}
                                                onChange={this.handleInputChange}
                                                autocapitalize={element.autocapitalize || 'none'}
                                                placeholder={element.placeholder}
                                                min={element.min_value}
                                                max={element.max_value}
                                            />
                                            {addendum}
                                        </td>
                                    </tr>
                                );
                            }
                    }
                }.bind(this));
            }.bind(this));

            let form_to_render = '';
            if (pages.length === 1) {
                form_to_render = (
                    <Card elevation={Elevation.ZERO}>
                        <HTMLTable striped={true} condensed={true} className={'form-table'}>
                            <tbody>
                                {pages[0]}
                            </tbody>
                        </HTMLTable>
                    </Card>
                );
            }
            else {
                let tabs_to_render = [];
                this.state.form.tabs.forEach(function(tab, tab_index) {
                    tabs_to_render.push(
                        <Tab id={tab["name"]} title={tab["name"]} panel={
                            <Card elevation={Elevation.ZERO}>
                                <H4>{tab["name"]}</H4>
                                <HTMLTable striped={true} condensed={true} className={'form-table'}>
                                    <tbody>
                                        {pages[tab_index]}
                                    </tbody>
                                </HTMLTable>
                            </Card>}>
                        </Tab>
                    );
                });
                form_to_render = (
                    <Tabs vertical={false}
                        onChange={(newTabId, prevTabId, event) => this.handleTabsChange(newTabId, prevTabId, event)}>
                        {tabs_to_render}
                    </Tabs>
                );
            }

            let closeButton = null;
            let saveButton = null;
            if (this.props.readonly) {
                closeButton = (<Button onClick={this.confirmCloseForm} text="OK" type={'button'}/>)
            } else {
                closeButton = (<Button onClick={this.confirmCloseForm} text="Cancel" type={'button'}/>);
                saveButton = (<Button
                    intent={Intent.SUCCESS}
                    text="SAVE"
                    type={'submit'}
                    onClick={this.submit}
                    disabled={!this.hasChangedSinceInitialState() || (this.state.newForm === true && pages.length >1 && this.visitAllNeeded() && Object.values(this.state.tabs_visited).some(val => val === false))}
                />);
            }
            let recordCompleteCheckbox = null;
            if (has_record_complete_checkbox) {
                let el = {column_name: 'wrc_record_complete'};
                let disabled = this.props.readonly;
                recordCompleteCheckbox = (<Checkbox
                    disabled={disabled}
                    label={has_record_complete_checkbox == true ? "Record complete" : has_record_complete_checkbox}
                    name={el.column_name}
                    checked={this.state.data[el.column_name] === 't'}
                    inline={true}
                    large={true}
                    key={el.column_name}
                    onChange={this.handleRecordComplete(el)}
                />);
            }
            return (
                <Dialog
                    isOpen={true}
                    onClose={this.confirmCloseForm}
                    canOutsideClickClose={false}
                    title={this.state.form.form_name}
                    id="web_form"
                    className={this.props.readonly ? "readonly" : ""}
                    style={{minWidth: 1100, minHeight: 600, maxWidth: 1400, maxHeight: 800, overflow: 'scroll'}} >
                    <div className={Classes.DIALOG_BODY}>
                        {form_to_render}
                    </div>
                    <div className={Classes.DIALOG_FOOTER}>
                        <div className={'bp3-dialog-footer-actions'} style={{float: 'right', padding: 10}}>
                            {recordCompleteCheckbox}
                            {closeButton}
                            {saveButton}
                        </div>
                        {this.visitAllMessage(this.state.tabs_visited)}
                    </div>
                </Dialog>
            );
        }
        else {
            return (<div>Loading...</div>);
        }
    }

    stringToDate = (dateString) => {
        // from: https://stackoverflow.com/questions/5619202/converting-a-string-to-a-date-in-javascript
        let parts =dateString.split('-');
        // Please pay attention to the month (parts[1]); JavaScript counts months from 0:
        // January - 0, February - 1, etc.
        let mydate = new Date(parts[0], parts[1] - 1, parts[2]);
        return mydate;
    }

    dateToString = (stringDate) => {
        if (stringDate === null) return '';
        let year= stringDate.getFullYear().toString();
        let month= (stringDate.getMonth() + 1).toString().length == 1 ?
            '0'+ (stringDate.getMonth() + 1).toString() :
            (stringDate.getMonth() + 1).toString();
        let day= (stringDate.getDate()).toString().length == 1 ?
            '0'+ (stringDate.getDate()).toString() :
            (stringDate.getDate()).toString();
        let newDateString = year + '-' + month + '-' + day;
        return newDateString;
    }

    formatDate = (inDate) => {
        let outDate= (inDate == null ? "" : this.dateToString(inDate));
        return outDate;
    }

    parseDate = (inStr) => {
        let outStr= this.stringToDate(inStr);
        return outStr;
    }

    handleDateChange = (newDateObj) =>{
        let newDateString = this.dateToString(newDateObj.newDate);
        var latest = Object.assign({}, this.state.data);
        latest[newDateObj.field_name] = newDateString;
        this.setState({data: latest, has_changed: true});
    }

    hasChangedSinceInitialState = () => {
        return this.state.has_changed && !WrcServer.deepEquals(this.original_data, this.state.data);
    }

    shouldBeDisabled(element, state, table_name=null, row_index=null) {
        let disabled;
        if (element.disable_if_any) {
            disabled = false;
        } else {
            disabled = true;
        }

        for (let i = 0; i < element.disable_if.length; i++) {
            let source = 'data';
            if (element.disable_if[i].source !== undefined) {
                source = element.disable_if[i].source;
            }

            let data;
            if (table_name == null || row_index == null) {
                data = state[source];
            } else {
                data = state[source][table_name][row_index];
            }

            if (element.disable_if_any) {
                if (this.matchAny(element.disable_if[i], data)) {
                    disabled = true;
                    break;
                }
            } else {
                if (this.matchNone(element.disable_if[i], data)) {
                    disabled = false;
                    break;
                }
            }
        }
        return disabled;
    }

    matchNotFound(condition, state, table_name=null, row_index=null) {
        let source = 'data';
        if (condition.source !== undefined) {
            source = condition.source;
        }

        let data;
        if (table_name == null || row_index == null) {
            data = state[source];
        } else {
            data = state[source][table_name][row_index];
        }
        return this.matchNone(condition, data);
    }

    matchAny(rule, data) {
        let field = data[rule.field_name];
        let match_found = rule.values.some(function(value) {
            if (value === '*') {
                return ((field !== undefined &&
                         field !== null &&
                         field !== '') ||
                        (Array.isArray(field) && field.length !== 0)
                );
            }
            if (Array.isArray(field)){
                return (field.includes(value));
            } else {
                return (value === field);
            }
        });
        return match_found;
    }

    matchNone(rule, data) {
        let field = data[rule.field_name];
        let match_not_found = rule.values.every(function(value) {
            if (value === '*') {
                return (field === undefined ||
                        field === null ||
                        field === '' ||
                        (Array.isArray(field) && field.length === 0)
                );
            }
            // show field if the condition is not defined (no data/unchecked)
            if (value === 'none') {
                return (field !== undefined);
            }
            if (Array.isArray(field)){
                return (!field.includes(value));
            } else {
                return (value !== field);
            }
        });
        return match_not_found;
    }

    handleTabsChange(newTabId, prevTabId) {
      let tabs_visited = {...this.state.tabs_visited};
      tabs_visited[prevTabId] = true;
      tabs_visited[newTabId] = true;
      this.setState({tabs_visited});
    }

    visitAllNeeded(){
        if(this.state.form.visitAll === "No"){
            return false;
        } else {
            return true;
        }
    }

    visitAllMessage(visit_obj) {
          if (Object.keys(visit_obj).length>1
                && this.state.newForm===true
                && !(this.state.form.visitAll === "No")) {
            let l1=Object.keys(visit_obj).filter(key => visit_obj[key] === false);
            let pages_to_visit= l1.map(page => {
              return (<li>{page}</li>);
            });
              return (
                  <Callout intent={Intent.PRIMARY} style={{width: '50%'}}>
                      You must also visit the following section(s) before saving:&nbsp;
                      <ul className={'bp3-list-unstyled'}>
                          {pages_to_visit}
                      </ul>
                  </Callout>
              );
          }
    }

    selectSuggest(element, disabled=false, fill=false, table_name=null, row_index=null) {
        let itemRenderer = (item, {handleClick, modifiers}) => {
            if (modifiers.matchesPredicate) {
                return (
                    <MenuItem
                        key={item.value}
                        label={item.label ? item.label : ''}
                        text={item.value}
                        onClick={handleClick}
                        active={modifiers.active}
                    />
                );
            } else {
                return null;
            }
        }
        let itemListPredicate = (query, items) => {
            return (items.filter((item) => `${item.value+'|'+item.label}`.toLowerCase().indexOf(query.toLowerCase()) >= 0))
        }
        let onItemSelect = (item) => {
            var latest = Object.assign({}, this.state.data);
            if (table_name == null || row_index == null) {
                latest[element.column_name] = item.value;
            } else {
                latest[table_name][row_index][element.column_name] = item.value;
            }
            this.setState({data: latest, has_changed: true});
        }
        let noResults = <MenuItem
            disabled={true}
            text="No match found."
        />
        let popoverProps = { boundary: 'viewport', popoverClassName: Classes.MINIMAL, usePortal: false };
        let selectedItem = (table_name == null || row_index == null)
            ? (this.state.data[element.column_name] === null || this.state.data[element.column_name] === undefined) ? {} : {"value":this.state.data[element.column_name]}
            : (this.state.data[table_name][row_index][element.column_name] === null || this.state.data[table_name][row_index][element.column_name] === undefined) ? {} : {"value":this.state.data[table_name][row_index][element.column_name]}
        if (!fill && 'fill' in element) {
            fill = element.fill;
        }
        let inputProps = {
            rightElement: <Tooltip2 content={<span>Click to clear entry</span>} placement="right"><Button minimal icon="cross" onClick={() => {onItemSelect({value: null})}} /></Tooltip2>
        };
        return (
            <Suggest
                items={element.responses}
                itemRenderer={itemRenderer}
                itemListPredicate={itemListPredicate}
                inputValueRenderer={item => item.value}
                noResults={noResults}
                onItemSelect={onItemSelect}
                popoverProps={popoverProps}
                selectedItem={selectedItem}
                fill={fill}
                resetOnSelect={true}
                resetOnClose={false}
                disabled={disabled}
                inputProps={inputProps}
            />);
    }

    addRecord = (event, element) => {
        var latest = Object.assign({}, this.state.data);
        if (!Array.isArray(latest[element.table_name])) {
            latest[element.table_name] = [];
        }
        latest[element.table_name].push({});
        this.setState({data: latest, has_changed: true});
    }

    afterSave = (content) => {
        if (this.props.fkey === undefined) {
            // only passed from the list_data holder to insert new rows on form save
            if (this.props.parentCallback) {
                this.props.parentCallback({'id': content, 'rec_saved_on': WrcGlobal.get_db_today(), ...this.data})
            }
            this.props.refresh();
        } else {
            this.props.refresh();
        }
        this.closeForm();
    }

    cancelSave = () => {
        this.setState({show_confirm: false});
    }

    confirmSave = () => {
        this.setState({show_confirm: false});
        this.save();
    }

    closeForm = () => {
        ReactDOM.unmountComponentAtNode(document.querySelector('#form'));
    };

    confirmCloseForm = (e) => {
        if (!this.hasChangedSinceInitialState()) {
            this.closeForm();
            return;
        }
        let alertBox = (
            <Alert
                cancelButtonText="No"
                confirmButtonText="Yes"
                isOpen={true}
                intent={Intent.WARNING}
                icon={IconNames.WARNING_SIGN}
                onCancel={this.cancelAlert}
                onConfirm={this.closeAlertAndForm}>
                <p>Close this form and LOSE all changes?</p>
            </Alert>
        );
        ReactDOM.render(alertBox, document.querySelector('#alertdiv'));
    }

    cancelAlert = () => {
        ReactDOM.unmountComponentAtNode(document.querySelector('#alertdiv'));
    }

    closeAlertAndForm = () => {
        this.closeForm();
        this.cancelAlert();
    }

    deleteRecord = (event, element, row_index) => {
        var latest = Object.assign({}, this.state.data);
        let newData = latest[element.table_name].filter((row, index) => {
            return index !== row_index;
        });
        latest[element.table_name]=newData;
        // table is not re-rendered correctly with just an array mutation
        // React needs for the data to be a new object to trigger re-render
        this.setState({data: {}}, () => this.setState({data: latest, has_changed: true}));
    }

    getForm = (content) => {
        if (Array.isArray(this.props.default_fields)) {
            this.props.default_fields.forEach(function(field) {
                content.data[field.name] = field.value;
            });
        }

        if (this.props.fkey !== undefined) {
            let reference_table_name;
            if (content.form.references.table_name === undefined) {
                reference_table_name = content.form.references;
            }
            else {
                reference_table_name = content.form.references.table_name;
            }
            // Only add REFERENCE_id if REFERENCE is different table than form table
            if (content.form.table_name !== reference_table_name) {
                content.data[reference_table_name + '_id'] = this.props.fkey;
            }
        }

        let newForm= false;
        if (!content.data.rec_saved_on) {
            newForm=true;
            if (content.form.has_record_complete_checkbox) {
                content.data['wrc_record_complete'] = 't';
            }
        }

        let tabs_visited = {};

        if (content.form.tabs.length === 1) {
            tabs_visited[0] = true;
        }
        else {
            content.form.tabs.forEach(function(tab, tab_index) {
                if (tab_index===0) {
                    tabs_visited[tab["name"]] = true;
                }
                else {
                    tabs_visited[tab["name"]] = false;
                }
            });
        }

        this.original_data = JSON.parse(JSON.stringify(content.data));
        this.setState({
            form: content.form,
            data: content.data,
            references: content.references,
            user: content.user,
            refresh: this.props.refresh,
            newForm: newForm,
            tabs_visited: tabs_visited,
            is_loaded: true});

    }

    handleCheckbox = (element, response) => (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);

        if (Array.isArray(latest[element.table_name]) && (!response.is_mutually_exclusive)) {
            let response_index = latest[element.table_name].indexOf(response.value);
            if (response_index > -1) {
                latest[element.table_name].splice(response_index, 1);
            }
            else {
                latest[element.table_name].push(response.value);
            }

            element.responses.forEach(function(resp) {
                if (resp.is_mutually_exclusive) {
                    let resp_index = latest[element.table_name].indexOf(resp.value);
                    if (resp_index > -1) {
                        latest[element.table_name].splice(resp_index, 1);
                    }
                }
            });

        }
        else {
            latest[element.table_name] = [response.value];
        }
        this.setState({data: latest, has_changed: true});
    }

    handleChildInputChange = (element, index, column) => (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);
        latest[element.table_name][index][column.column_name] = e.target.autocapitalize === 'characters' ? e.target.value.toUpperCase() : e.target.value;
        this.setState({data: latest, has_changed: true});
    }

    handleNotReported = (element) => (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);
        if (latest[element.column_name + '_not_reported'] === 't') {
            latest[element.column_name + '_not_reported'] = 'f';
        }
        else {
            latest[element.column_name] = '';
            latest[element.column_name + '_not_reported'] = 't';
        }
        this.setState({data: latest, has_changed: true});
    }

    handleNotReportedInTable = (element, row_index, column) => (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);
        if (latest[element.table_name][row_index][column.column_name + '_not_reported'] === 't') {
            latest[element.table_name][row_index][column.column_name + '_not_reported'] = 'f';
        }
        else {
            latest[element.table_name][row_index][column.column_name] = '';
            latest[element.table_name][row_index][column.column_name + '_not_reported'] = 't';
        }
        this.setState({data: latest, has_changed: true});
    }

    handleRecordComplete = (element) => (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);
        if (latest[element.column_name] === 't') {
            latest[element.column_name] = 'f';
        } else {
            latest[element.column_name] = 't';
        }
        this.setState({data: latest, has_changed: true});
    }

    handleBooleanChange(e, element, row_index=null, column=null){
        var latest = Object.assign({}, this.state.data);
        if(row_index !== null && column !== null){
            latest[element.table_name][row_index][column.column_name]= (latest[element.table_name][row_index][column.column_name] ? !(latest[element.table_name][row_index][column.column_name]) : true);
            this.setState({data: latest, has_changed: true});
        } else {
            latest[element.column_name]= (latest[element.column_name] ? !(latest[element.column_name]) : true);
            this.setState({data: latest, has_changed: true});
        }
    }

    handleInputChange = (e) => {
        e.persist();
        var latest = Object.assign({}, this.state.data);
        latest[e.target.name] = e.target.autocapitalize === 'characters' ? e.target.value.toUpperCase() : e.target.value;
        this.setState({data: latest, has_changed: true});
    }

    isChecked = (table_name, value) => {
        return (Array.isArray(this.state.data[table_name]) && this.state.data[table_name].indexOf(value) > -1);
    }

    openDocument = (content) => {
        var display = window.open("", "_blank");
        display.document.write('<div style="height: 11.0in; width: 8.5in; margin: 0.25in;">' + content + '</div>');
    }

    recordData = (data) => {
        var latest = {...this.state.data, ...data};
        this.setState({data: latest, has_changed: true});
    }

    save = () => {
        this.do_not_post.forEach(function(column_name) {
            delete this.data[column_name];
        }.bind(this));

        var post = {
            table_schema: this.state.form.table_schema,
            table_name: this.state.form.table_name,
            original_data: this.original_data,
            data: this.data
        }

        WrcServer.post('save-form',
                post,
                (content) => this.afterSave(content)
        );
    }

    submit = (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.data = Object.assign({}, this.state.data);
        this.do_not_post = [];

        this.state.form.tabs.forEach(function(tab) {
            tab.elements.forEach(function(element) {
                if (element.element_type === 'timestamp' || 
                    element.element_type === 'datestamp' ||
                    element.element_type === 'display') {
                    return;
                }
                if (element.element_type === 'today' && this.data[element.column_name] === undefined) {
                    this.data[element.column_name] = WrcGlobal.get_db_today();
                }
                if (element.element_type === 'boolean' && this.data[element.column_name] === undefined) {
                    this.data[element.column_name] = false;
                }
                let shouldBeWiped = false;
                if (element.show_if !== undefined) {
                    for (let i = 0; i < element.show_if.length; i++) {
                        if (this.matchNotFound(element.show_if[i], this.state)) {
                            shouldBeWiped = true;
                            break;
                        }
                    }
                }
                if (element.disable_if !== undefined && this.shouldBeDisabled(element, this.state)) {
                    shouldBeWiped = true;
                }
                if (shouldBeWiped) {
                    if (element.column_name !== undefined) {
                        this.data[element.column_name] = '';
                        if (element.include_not_reported) {
                            this.data[element.column_name + '_not_reported'] = '';
                        }
                    }
                    if (element.table_name !== undefined) {
                        this.data[element.table_name] = [''];
                    }
                }

                /* Don't save checked checkboxes that have been hidden or disabled */
                if (element.element_type === 'checkbox' && element.table_name !== undefined && Array.isArray(this.data[element.table_name])) {
                    element.responses.forEach(function(response) {
                        let shouldBeWiped = false;
                        if (response.show_if !== undefined) {
                            for (let i = 0; i < response.show_if.length; i++) {
                                if (this.matchNotFound(response.show_if[i], this.state)) {
                                    shouldBeWiped = true;
                                }
                            }
                        }
                        if (response.disable_if !== undefined && this.shouldBeDisabled(response, this.state)) {
                            shouldBeWiped = true;
                        }
                        if (shouldBeWiped) {
                            let response_index = this.data[element.table_name].indexOf(response.value);
                            if (response_index > -1) {
                                this.data[element.table_name].splice(response_index, 1);
                            }
                        }
                    }.bind(this));
                }

                if (element.table_name !== undefined && (!this.data[element.table_name] || (Array.isArray(this.data[element.table_name]) && this.data[element.table_name].length < 1))) {
                    this.data[element.table_name] = [''];
                }
                // convert empty table arrays to arrays of a single empty string so this element doesn't disappear on server side
                if (element.table_name !== undefined && (!this.original_data[element.table_name] || (Array.isArray(this.original_data[element.table_name]) && this.original_data[element.table_name].length < 1))) {
                    this.original_data[element.table_name] = [''];
                }
                if (element.element_type === 'table' && element.table_name !== undefined && Array.isArray(this.state.data[element.table_name])) {
                    // eslint-disable-next-line
                    this.state.data[element.table_name].map(function(row, row_index) {
                        // eslint-disable-next-line
                        element.columns.map(function(column) {
                            if (column.element_type === 'timestamp' || element.element_type === 'datestamp') {
                                return;
                            }
                            if (column.element_type === 'today' && row[column.column_name] === undefined) {
                                row[column.column_name] = WrcGlobal.get_db_today();
                            }
                            if (column.element_type === 'boolean' && row[column.column_name] === undefined) {
                                row[column.column_name] = false;
                            }
                            if (column.show_if !== undefined) {
                                for (let i = 0; i < column.show_if.length; i++) {
                                    if (this.matchNotFound(column.show_if[i], this.state,  element.table_name, row_index)) {
                                        if (column.column_name !== undefined) {
                                            row[column.column_name] = '';
                                            if (column.include_not_reported) {
                                                row[column.column_name + '_not_reported'] = '';
                                            }
                                        }
                                        if (column.table_name !== undefined) {
                                            row[column.table_name] = [''];
                                        }
                                        break;
                                    }
                                }
                            }
                        }.bind(this));
                    }.bind(this));
                }

                if (element.post === false) {
                    this.do_not_post.push(element.column_name);
                }
            }.bind(this));
        }.bind(this));

        if (this.state.form.document !== undefined) {
            var content = this.state.form.document;
            this.state.form.tabs.forEach(function(tab) {
                tab.elements.forEach(function(element) {
                    content = content.replace(new RegExp("~" + element.column_name + "~", 'g'), this.data[element.column_name]);
                }.bind(this));
            }.bind(this));
            this.setState({show_confirm: true});
            this.openDocument(content);
        }
        else {
            this.save();
        }
    }
}

WrcForm.propTypes = {
    form_id: PropTypes.number.isRequired,
    record_id: PropTypes.number,
    refresh: PropTypes.func,
    fkey: PropTypes.number,
    component_index: PropTypes.number,
    //configure_component: PropTypes.func
};
